import type { FieldDef } from '../components/Field';
import type { DrawerType } from '../store/app';

// US states + DC, for structured address selects.
export const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

export interface FormConfig {
  title: (edit: boolean) => string;
  sub: (edit: boolean) => string;
  delLabel: string;
  endpoint: string; // REST collection
  fields: (FieldDef & { optionsSource?: string })[];
}

// Form definitions for the CRUD drawers, mirroring buildFields() in the
// prototype but keyed to real entity fields. `optionsSource` lets a select
// pull its options at runtime (data points group or another collection).
export const FORMS: Partial<Record<DrawerType, FormConfig>> = {
  person: {
    title: (e) => (e ? 'Edit staff member' : 'Add staff member'),
    sub: (e) => (e ? 'Update profile, location & rate' : 'Create a staff or contractor profile'),
    delLabel: 'Delete person',
    endpoint: '/people',
    fields: [
      { kind: 'text', key: 'name', label: 'Full name', placeholder: 'e.g. Dana Cole', full: true },
      { kind: 'select', key: 'role', label: 'Role', placeholder: 'Select role', optionsSource: 'Roles' },
      { kind: 'segment', key: 'type', label: 'Type', options: ['FTE', 'Contractor'] },
      { kind: 'text', key: 'email', label: 'Email', placeholder: 'name@company.com' },
      { kind: 'text', key: 'phone', label: 'Phone', placeholder: '+1 …' },
      { kind: 'text', key: 'addr_line1', label: 'Home / mailing address', placeholder: 'Street address', full: true },
      { kind: 'text', key: 'addr_line2', label: 'Address line 2', placeholder: 'Apt, suite, unit (optional)', full: true },
      { kind: 'text', key: 'city', label: 'City', placeholder: 'City' },
      { kind: 'select', key: 'state', label: 'State', placeholder: 'State', options: US_STATES },
      { kind: 'text', key: 'zip', label: 'ZIP code', placeholder: '5-digit ZIP' },
      { kind: 'text', key: 'day_rate', label: 'Day rate (USD)', placeholder: '$ / day — contractors only' },
      { kind: 'chips', key: 'skills', label: 'Skills', full: true },
    ],
  },
  client: {
    title: (e) => (e ? 'Edit client' : 'New client'),
    sub: (e) => (e ? 'Update organization details' : 'Add a client organization'),
    delLabel: 'Archive client',
    endpoint: '/clients',
    fields: [
      { kind: 'text', key: 'name', label: 'Company name', placeholder: 'e.g. Helios Energy', full: true },
      { kind: 'select', key: 'industry', label: 'Industry', placeholder: 'Select industry', optionsSource: 'Industries' },
      { kind: 'select', key: 'status', label: 'Status', options: ['planning', 'active', 'at-risk', 'done'] },
      { kind: 'text', key: 'primary_contact', label: 'Primary contact', placeholder: 'Full name' },
      { kind: 'text', key: 'contact_email', label: 'Contact email', placeholder: 'name@company.com' },
      { kind: 'text', key: 'hq_city', label: 'HQ city', placeholder: 'City' },
      { kind: 'text', key: 'since', label: 'Client since', placeholder: 'Year' },
    ],
  },
  project: {
    title: (e) => (e ? 'Edit project' : 'New project'),
    sub: (e) => (e ? 'Update scope, owner & status' : 'Create a project on the board'),
    delLabel: 'Delete project',
    endpoint: '/projects',
    fields: [
      { kind: 'text', key: 'name', label: 'Project name', placeholder: 'e.g. Retail rollout', full: true },
      { kind: 'select', key: 'type', label: 'Project type', placeholder: 'Select type', optionsSource: 'Project types' },
      { kind: 'select', key: 'client_id', label: 'Client', placeholder: 'Select client', optionsSource: 'clients' },
      { kind: 'select', key: 'contract_id', label: 'Contract / PO', placeholder: 'None', optionsSource: 'contracts' },
      { kind: 'select', key: 'status', label: 'Status', options: ['planning', 'active', 'at-risk', 'done'] },
      { kind: 'segment', key: 'priority', label: 'Priority', options: ['Low', 'Medium', 'High'] },
      { kind: 'text', key: 'due_date', label: 'Due date', placeholder: 'Pick a date' },
      { kind: 'text', key: 'pct', label: 'Progress %', placeholder: '0–100' },
      { kind: 'chips', key: 'tags', label: 'Tags', full: true },
      { kind: 'textarea', key: 'notes', label: 'Notes', placeholder: 'Optional context…', full: true },
    ],
  },
  contact: {
    title: (e) => (e ? 'Edit contact' : 'Add contact'),
    sub: (e) => (e ? 'Update point of contact' : 'Add a point of contact for this client'),
    delLabel: 'Remove contact',
    endpoint: '/contacts',
    fields: [
      { kind: 'text', key: 'name', label: 'Full name', placeholder: 'e.g. Hannah Booth', full: true },
      { kind: 'text', key: 'title', label: 'Job title', placeholder: 'e.g. Operations Lead' },
      { kind: 'select', key: 'client_id', label: 'Client', placeholder: 'Select client', optionsSource: 'clients' },
      { kind: 'text', key: 'email', label: 'Email', placeholder: 'name@company.com' },
      { kind: 'text', key: 'phone', label: 'Phone', placeholder: '+1 …' },
      { kind: 'segment', key: 'primary', label: 'Primary contact', options: ['No', 'Yes'] },
      { kind: 'textarea', key: 'notes', label: 'Notes', placeholder: 'Role, best time to reach, etc.', full: true },
    ],
  },
  profile: {
    title: () => 'Edit my profile',
    sub: () => 'Your contact details & payment info',
    delLabel: '',
    endpoint: '/profile',
    fields: [
      { kind: 'text', key: 'name', label: 'Full name', placeholder: 'Full name', full: true },
      { kind: 'text', key: 'role', label: 'Role' },
      { kind: 'segment', key: 'type', label: 'Employment', options: ['FTE', 'Contractor'] },
      { kind: 'text', key: 'email', label: 'Email' },
      { kind: 'text', key: 'phone', label: 'Phone' },
      { kind: 'text', key: 'addr_line1', label: 'Home / mailing address', placeholder: 'Street address', full: true },
      { kind: 'text', key: 'addr_line2', label: 'Address line 2', placeholder: 'Apt, suite, unit (optional)', full: true },
      { kind: 'text', key: 'city', label: 'City' },
      { kind: 'select', key: 'state', label: 'State', placeholder: 'State', options: US_STATES },
      { kind: 'text', key: 'zip', label: 'ZIP code', placeholder: '5-digit ZIP' },
      { kind: 'text', key: 'emergency_contact', label: 'Emergency contact', full: true },
      { kind: 'select', key: 'payment_method', label: 'Payment method', options: ['ACH transfer', 'Wire', 'Card', 'Check'] },
      { kind: 'text', key: 'account_last4', label: 'Account (last 4)' },
    ],
  },
};

// Defaults used when creating a new record.
export const FORM_DEFAULTS: Record<string, Record<string, unknown>> = {
  person: { type: 'FTE', status: 'active', skills: [] },
  client: { status: 'planning' },
  project: { type: 'General', status: 'planning', priority: 'Medium', pct: '0', tags: [] },
  contact: { primary: 'No' },
};
